import React, { useState, useEffect } from "react";
import UserService from "../services/user.service"; // Assuming UserService handles API calls
import { Alert, Table, Button, Container, Modal, Form, ButtonGroup, Pagination, Spinner } from 'react-bootstrap';
import { FaEdit, FaTrash } from 'react-icons/fa';

const CountryManager = () => {
  const [countries, setCountries] = useState([]);
  const [formData, setFormData] = useState({
    country_name: '',
    country_code: '',
    tax_id: '',
    currency_id: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiErrors, setApiErrors] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('country_name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);

  const [taxes, setTaxes] = useState([]);
  const [currencies, setCurrencies] = useState([]);

  useEffect(() => {
    
    loadCountries();
    loadTaxes();
    loadCurrencies();
  }, [currentPage, searchTerm, sortField, sortOrder]);

  const loadCountries = async () => {
    setApiErrors('');
    setErrors({});
    setLoading(true);
    try {
      const response = await UserService.getAllCountries({
        page: currentPage,
        search: searchTerm,
        sort: sortField,
        order: sortOrder
      });
      setCountries(response.data.countries);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      setApiErrors('Laden der Länder fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const loadTaxes = async () => {
    
    try {
      const response = await UserService.getAllTaxes(); // Assuming an API call for taxes
      setTaxes(response.data);
    } catch (error) {
      setApiErrors('Laden der Steuern fehlgeschlagen');
    }
  };

  const loadCurrencies = async () => {
    try {
      const response = await UserService.getAllCurrencies(); // Assuming an API call for currencies
      setCurrencies(response.data);
    } catch (error) {
      setApiErrors('Laden der Währungen fehlgeschlagen');
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to the first page when searching
  };

  const handleSortChange = (field) => {
    const newSortOrder = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortOrder(newSortOrder);
  };

  const handleCreateClick = () => {
    setFormData({
    
      country_name: '',
      country_official_code: '',
      tax_id: '',
      currency_id: ''
    });
    setApiErrors('');
    setErrors({});
    setShowCreateModal(true);
  };

  const handleEditClick = (country) => {
    setSelectedCountry(country);
    console.log(country);
    setFormData({
      country_name: country.country_name,
      country_official_code: country.country_official_code,
      tax_id: country.tax_id,
      currency_id: country.currency_id
    });
    setApiErrors('');
    setErrors({});
    setShowEditModal(true);
  };

  const handleDeleteClick = async (country_id) => {
    if (window.confirm('Sind Sie sicher, dass Sie dieses Land löschen möchten?')) {
      try {
        await UserService.deleteCountry(country_id);
        loadCountries(); // Reload countries after successful deletion
      } catch (error) {
        // Extract the error message from the Axios response
        if (error.response && error.response.data && error.response.data.message) {
          setApiErrors(error.response.data.message); // Use backend error message
        } else {
          setApiErrors('Löschen des Landes fehlgeschlagen'); // Fallback message
        }
      }
    }
  };
  

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    validateField(e.target.name, e.target.value);
  };

  const validateField = (fieldName, value) => {
    let fieldErrors = {};

    switch (fieldName) {
      case 'country_name':
        if (!value) fieldErrors.country_name = 'Ländername ist ein Pflichtfeld';
        break;
      case 'country_official_code':
        if (!value) fieldErrors.country_official_code = 'Ländercode ist ein Pflichtfeld';
        break;
      case 'tax_id':
        if (!value) fieldErrors.tax_id = 'Steuer ist ein Pflichtfeld';
        break;
      case 'currency_id':
        if (!value) fieldErrors.currency_id = 'Währung ist ein Pflichtfeld';
        break;
      default:
        break;
    }

    setErrors({ ...errors, ...fieldErrors });
  };

  const isFormValid = () => {
    return Object.keys(errors).length === 0 && formData.country_name && formData.country_official_code && formData.tax_id && formData.currency_id;
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setApiErrors('');
    setErrors({});
    if (!isFormValid()) {
      setLoading(false);
      setApiErrors('Bitte korrigieren Sie die Fehler im Formular.');
      return;
    }

    try {
      await UserService.createCountry(formData);
      setShowCreateModal(false);
      loadCountries();
    } catch (error) {
      setApiErrors('Erstellen des Landes fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubmit = async (e) => {
    setApiErrors('');
    setErrors({});
    e.preventDefault();
    setLoading(true);

    if (!isFormValid()) {
      setLoading(false);
      setApiErrors('Bitte korrigieren Sie die Fehler im Formular.');
      return;
    }

    try {
      await UserService.updateCountry(selectedCountry.country_id, formData);
      setShowEditModal(false);
      loadCountries();
    } catch (error) {
      setApiErrors('Aktualisierung des Landes fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <h1>Länderverwaltung</h1>
      {apiErrors && <Alert variant="danger">{apiErrors}</Alert>}
      <Button onClick={handleCreateClick}>Land erstellen</Button>
      <Form.Group controlId="searchCountry">
        <Form.Label>Länder durchsuchen</Form.Label>
        <Form.Control
          type="text"
          placeholder="Nach Ländernamen suchen"
          value={searchTerm}
          onChange={handleSearchChange}
        />
      </Form.Group>
      {loading && <Spinner animation="border" />}
      <Table striped bordered hover>
        <thead>
          <tr>
            <th onClick={() => handleSortChange('country_id')}>Länder-ID</th>
            <th onClick={() => handleSortChange('country_name')}>Ländername</th>
            <th onClick={() => handleSortChange('country_official_code')}>Ländercode</th>
            <th>MwSt.</th>
            <th>Währung</th>
            <th>Aktionen</th>
          </tr>
        </thead>
        <tbody>
          {countries.map(country => (
            <tr key={country.country_id}>
              <td>{country.country_id}</td>
              <td>{country.country_name}</td>
              <td>{country.country_official_code}</td>
              <td>{country.tax?.tax_name || 'N/A'}</td>
              <td>{country.t_currencies?.currency_name || 'N/A'}</td>
              
                    <td>
                <Button variant="warning" onClick={() => handleEditClick(country)} title="Bearbeiten" aria-label="Bearbeiten"><FaEdit /></Button>{' '}
                <Button variant="danger" onClick={() => handleDeleteClick(country.country_id)} title="Löschen" aria-label="Löschen"><FaTrash /></Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <Pagination>
        {[...Array(totalPages)].map((_, index) => (
          <Pagination.Item key={index} active={index + 1 === currentPage} onClick={() => setCurrentPage(index + 1)}>
            {index + 1}
          </Pagination.Item>
        ))}
      </Pagination>

      {/* Create Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Neues Land erstellen</Modal.Title>
        </Modal.Header>
        {apiErrors && <Alert variant="danger">{apiErrors}</Alert>}
        <Modal.Body>
          <Form onSubmit={handleCreateSubmit}>
            <Form.Group controlId="country_name">
              <Form.Label>Ländername</Form.Label>
              <Form.Control
                type="text"
                name="country_name"
                value={formData.country_name}
                onChange={handleChange}
                isInvalid={!!errors.country_name}
                required
              />
              <Form.Control.Feedback type="invalid">{errors.country_name}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group controlId="country_code">
              <Form.Label>Ländercode</Form.Label>
              <Form.Control
                type="text"
                name="country_official_code"
                value={formData.country_official_code}
                onChange={handleChange}
                isInvalid={!!errors.country_official_code}
                required
              />
              <Form.Control.Feedback type="invalid">{errors.country_official_code}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group controlId="tax_id">
              <Form.Label>MwSt.-Satz auswählen</Form.Label>
              <Form.Control as="select" name="tax_id" value={formData.tax_id} onChange={handleChange} required>
                <option value="">Auswählen...</option>
                {taxes.map(tax => (
                  <option key={tax.tax_id} value={tax.tax_id}>{tax.tax_name}</option>
                ))}
              </Form.Control>
            </Form.Group>

            <Form.Group controlId="currency_id">
              <Form.Label>Währung auswählen</Form.Label>
              <Form.Control as="select" name="currency_id" value={formData.currency_id} onChange={handleChange} required>
                <option value="">Auswählen...</option>
                {currencies.map(currency => (
                  <option key={currency.currency_id} value={currency.currency_id}>{currency.currency_name}</option>
                ))}
              </Form.Control>
            </Form.Group>

            <Button variant="primary" type="submit" disabled={loading || !isFormValid()}>
              {loading ? 'Wird gespeichert...' : 'Land speichern'}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Edit Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Land bearbeiten</Modal.Title>
        </Modal.Header>
        {apiErrors && <Alert variant="danger">{apiErrors}</Alert>}
        <Modal.Body>
          <Form onSubmit={handleEditSubmit}>
            <Form.Group controlId="country_name">
              <Form.Label>Ländername</Form.Label>
              <Form.Control
                type="text"
                name="country_name"
                value={formData.country_name}
                onChange={handleChange}
                isInvalid={!!errors.country_name}
                required
              />
              <Form.Control.Feedback type="invalid">{errors.country_name}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group controlId="country_code">
              <Form.Label>Ländercode</Form.Label>
              <Form.Control
                type="text"
                name="country_official_code"
                value={formData.country_official_code}
                onChange={handleChange}
                isInvalid={!!errors.country_official_code}
                required
              />
              <Form.Control.Feedback type="invalid">{errors.country_official_code}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group controlId="tax_id">
              <Form.Label>Steuer auswählen</Form.Label>
              <Form.Control as="select" name="tax_id" value={formData.tax_id} onChange={handleChange} required>
                <option value="">Auswählen...</option>
                {taxes.map(tax => (
                  <option key={tax.tax_id} value={tax.tax_id}>{tax.tax_name}</option>
                ))}
              </Form.Control>
            </Form.Group>

            <Form.Group controlId="currency_id">
              <Form.Label>Währung auswählen</Form.Label>
              <Form.Control as="select" name="currency_id" value={formData.currency_id} onChange={handleChange} required>
                <option value="">Auswählen...</option>
                {currencies.map(currency => (
                  <option key={currency.currency_id} value={currency.currency_id}>{currency.currency_name}</option>
                ))}
              </Form.Control>
            </Form.Group>

            <Button variant="primary" type="submit" disabled={loading || !isFormValid()}>
              {loading ? 'Wird gespeichert...' : 'Änderungen speichern'}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default CountryManager;